import React from 'react';
import { X, Swords, Trophy, Volume2, VolumeX, Gauge, LogOut, Shield, Siren, Crosshair, DollarSign } from 'lucide-react';
import { TEAM_COLOR } from './rtbBoard';

function Modal({ children, onClose, testId, wide }) {
  return (
    <div className="absolute inset-0 z-[100000] flex items-center justify-center bg-black/70 backdrop-blur-sm p-3" onClick={onClose}>
      <div data-testid={testId} onClick={(e) => e.stopPropagation()}
        className={`rtb-pop relative w-full ${wide ? 'max-w-lg' : 'max-w-sm'} max-h-full overflow-y-auto rounded-xl border border-[#C9A84C]/50 bg-[#0c0b0a]/95 p-5 shadow-[0_0_40px_rgba(201,168,76,0.25)]`}>
        {onClose && <button type="button" data-testid={`${testId}-close`} onClick={onClose} aria-label="Close" className="absolute right-3 top-3 text-white/60 hover:text-white"><X size={18} /></button>}
        {children}
      </div>
    </div>
  );
}

export function RulesModal({ rules, city, onClose }) {
  const legend = [[Shield, 'Safe Zone — no captures here', '#60A5FA'], [Siren, 'Police Checkpoint — +heat (Connector bribes)', '#FACC15'], [Crosshair, 'Ambush — lose cash (Enforcer fights off)', '#F87171'], [DollarSign, 'Territory — earn cash (Earner doubles)', '#4ADE80']];
  return (
    <Modal onClose={onClose} testId="rtb-rules-modal" wide>
      <h3 className="rtb-display text-2xl text-[#C9A84C] tracking-wider">GAME RULES</h3>
      {city && (
        <div data-testid="rtb-rules-city-perk" className="mt-3 rounded-lg border border-[#C9A84C]/50 bg-[#C9A84C]/10 p-3">
          <div className="text-[10px] tracking-[0.25em] text-white/60">{city.name.toUpperCase()} BOARD PERK</div>
          <div className="rtb-display text-xl text-[#F3DC8A] tracking-wider">{city.perk}</div>
          <p className="text-sm text-white/80">{city.perk_desc}</p>
        </div>
      )}
      <ol className="mt-3 space-y-2 list-decimal pl-5 text-sm text-white/85">{rules.map((r) => <li key={r}>{r}</li>)}</ol>
      <h4 className="rtb-display text-lg text-white mt-5 tracking-wider">TERRITORY LEGEND</h4>
      <ul className="mt-2 space-y-1.5">{legend.map(([I, t, c]) => <li key={t} className="flex items-center gap-2 text-sm text-white/80"><I size={16} color={c} />{t}</li>)}</ul>
    </Modal>
  );
}

export function CombatModal({ ev, onClose }) {
  const a = TEAM_COLOR[ev.attacker];
  const d = TEAM_COLOR[ev.defender];
  const win = ev.winner === ev.attacker;
  return (
    <Modal onClose={onClose} testId="rtb-combat-modal">
      <div className="text-center">
        <Swords size={40} className="mx-auto text-[#EF4444] rtb-shake" />
        <h3 className="rtb-display text-3xl text-white mt-2 tracking-wider">COMBAT</h3>
        <p className="text-xs text-white/60 mt-1">Three straight captures. It's on.</p>
        <div className="flex items-center justify-center gap-6 mt-4">
          {[[ev.attacker, a, ev.a_roll, ev.a_bonus], [ev.defender, d, ev.d_roll, ev.d_bonus]].map(([t, c, r, b]) => (
            <div key={t} className="text-center">
              <div className="rtb-display text-lg" style={{ color: c }}>{t.toUpperCase()}</div>
              <div className="rtb-display text-4xl text-white">{r + b}</div>
              <div className="text-[10px] text-white/50">roll {r}{b ? ` +${b} enforcer` : ''}</div>
            </div>
          ))}
        </div>
        <p data-testid="rtb-combat-result" className="mt-4 text-sm" style={{ color: win ? a : d }}>
          {ev.winner.toUpperCase()} wins the fight{ev.sent_back ? ` · ${ev.sent_back.team.toUpperCase()} crew sent back to base` : ''}{win ? ' · +$5,000 bounty' : ''}
        </p>
        <button type="button" data-testid="rtb-combat-continue" onClick={onClose} className="rtb-btn-gold mt-4 px-6 h-10 rounded-full rtb-display tracking-wider">CONTINUE</button>
      </div>
    </Modal>
  );
}

export function WinModal({ match, myTeam, onDone }) {
  const w = match.winner;
  const mine = w === myTeam;
  const me = match.teams[myTeam] || match.teams[w];
  return (
    <Modal testId="rtb-win-modal">
      <div className="text-center">
        <Trophy size={44} className="mx-auto" color={TEAM_COLOR[w]} />
        <h3 className="rtb-display text-3xl mt-2 tracking-wider" style={{ color: TEAM_COLOR[w] }} data-testid="rtb-winner">{w.toUpperCase()} RUNS THE BLOCK</h3>
        <p className="text-sm text-white/70 mt-2">{mine ? 'All four of your crew made it home.' : `${match.seats[w].name} brought all four crew home first.`}</p>
        {!myTeam && <p className="text-xs text-white/50 mt-1">AI battle preview · no cash at stake</p>}
        <div className="mt-4 grid grid-cols-2 gap-2 text-left text-sm">
          <div className="rounded-md bg-white/5 p-2"><div className="text-[10px] text-white/50">POT</div><div className="text-white" data-testid="rtb-win-pot">{!myTeam ? '$0' : mine ? `+$${(match.wager * 4).toLocaleString()}` : `-$${match.wager.toLocaleString()}`}</div></div>
          <div className="rounded-md bg-white/5 p-2"><div className="text-[10px] text-white/50">BLOCK CASH</div><div className="text-white">{me.cash >= 0 ? '+' : '-'}${Math.abs(me.cash).toLocaleString()}</div></div>
          <div className="rounded-md bg-white/5 p-2"><div className="text-[10px] text-white/50">CAPTURES</div><div className="text-white">{me.captures}</div></div>
          <div className="rounded-md bg-white/5 p-2"><div className="text-[10px] text-white/50">HEAT</div><div className="text-white">+{me.heat}</div></div>
        </div>
        <button type="button" data-testid="rtb-play-again" onClick={onDone} className="rtb-btn-gold mt-5 px-6 h-11 rounded-full rtb-display tracking-wider">BACK TO THE TABLE</button>
      </div>
    </Modal>
  );
}

export function MenuModal({ muted, onMute, speed, setSpeed, auto, onAuto, spectate, onLeave, onClose, code }) {
  const row = 'w-full flex items-center gap-3 rounded-lg border border-white/15 bg-white/5 px-4 h-12 text-sm text-white hover:bg-white/10 transition-colors';
  return (
    <Modal onClose={onClose} testId="rtb-menu-modal">
      <h3 className="rtb-display text-2xl text-[#C9A84C] tracking-wider mb-4">MENU</h3>
      {code && <p className="text-xs text-white/60 mb-3">Table code: <span className="text-white tracking-widest" data-testid="rtb-menu-code">{code}</span></p>}
      <div className="space-y-2">
        <button type="button" data-testid="rtb-toggle-sound" onClick={onMute} className={row}>{muted ? <VolumeX size={18} /> : <Volume2 size={18} />} Sound {muted ? 'off' : 'on'}</button>
        <div className={row}><Gauge size={18} /> Speed
          <span className="ml-auto flex gap-1">{[1, 2, 4].map((v) => (
            <button key={v} type="button" data-testid={`rtb-menu-speed-${v}x`} onClick={() => setSpeed(v)} className={`px-2.5 h-8 rounded-md ${speed === v ? 'bg-[#C9A84C] text-black' : 'bg-white/10'}`}>{v}x</button>
          ))}</span>
        </div>
        {!spectate && <button type="button" data-testid="rtb-menu-auto" onClick={onAuto} className={row}><Gauge size={18} /> Auto-play my turns: {auto ? 'On' : 'Off'}</button>}
        <button type="button" data-testid="rtb-leave-btn" onClick={onLeave} className={`${row} text-[#F87171]`}><LogOut size={18} /> {spectate ? 'Stop spectating' : 'Leave match (forfeit buy-in)'}</button>
      </div>
    </Modal>
  );
}

export function Toasts({ toasts }) {
  const tone = { capture: '#F87171', bad: '#FB923C', good: '#4ADE80', info: '#C9A84C' };
  return (
    <div className="absolute left-1/2 -translate-x-1/2 top-2 z-[99999] flex flex-col items-center gap-1.5 pointer-events-none w-[92%] max-w-md" data-testid="rtb-toasts">
      {toasts.map((t) => (
        <div key={t.id} className="rtb-toast rounded-full bg-black/85 px-4 py-1.5 text-xs sm:text-sm text-white text-center border" style={{ borderColor: tone[t.tone] }}>{t.text}</div>
      ))}
    </div>
  );
}
